/**
 * The fan's membership marker: this browser's copy of the server's answer about their latest
 * join or leave, so a page knows whether it has 3Halves work to resume WITHOUT asking.
 *
 * **It exists to keep the status path free.** The alternative -- read `/status` on every page to
 * learn whether a leg is due -- would spend the per-fan throttle bucket outlier-stats shares and add
 * a request to every signed-in page view at match peaks. Instead every begin, advance and status
 * answer writes the summary here, and resume reads it.
 *
 * **The server stays the source of truth.** Nothing here decides a state: a stale or lost marker
 * costs a resume that happens a page late, never a wrong outcome, because every leg starts by
 * reading 3Halves and every report is re-judged by the server.
 *
 * **Fan-scoped by NAME and in localStorage**, not sessionStorage: a second tab must see that the
 * first one's leave superseded its join (the leg fence reads this), and a fan returning tomorrow
 * must see a leg that is still due. See ../perFanStorage.test.js, which accounts for the family.
 *
 * Everything is injected so marker.test.js runs under `node --test` with no browser.
 */

/** Key family; one entry per fan, `urc.loyalty.membership.<sub>`. */
const PREFIX = 'urc.loyalty.membership';

/** Key family for an unacknowledged abort, `urc.loyalty.membershipAbort.<sub>`. */
const ABORT_PREFIX = 'urc.loyalty.membershipAbort';

/**
 * @param {string} sub The fan's Auth0 `sub`.
 * @return {string} That fan's key.
 */
function keyOf( sub ) {
	return `${ PREFIX }.${ sub }`;
}

/**
 * @param {Object}   options            Wiring.
 * @param {Function} options.getStorage `() => Storage|null` -- localStorage, or null where the
 *                                      browser refuses it (a private window, blocked site data).
 * @return {{read: Function, write: Function, patch: Function, clear: Function}} The marker.
 */
export function createMarker( { getStorage } ) {
	/**
	 * @return {Storage|null} Storage, or null.
	 */
	function storage() {
		try {
			return getStorage() || null;
		} catch ( error ) {
			return null;
		}
	}

	return {
		/**
		 * @param {string} sub The fan.
		 * @return {Object|null} The stored summary, or null.
		 */
		read( sub ) {
			const store = storage();

			if ( ! store || ! sub ) {
				return null;
			}

			try {
				const value = JSON.parse( store.getItem( keyOf( sub ) ) || 'null' );

				return value && typeof value === 'object' && typeof value.txn === 'string'
					? value
					: null;
			} catch ( error ) {
				return null;
			}
		},

		/**
		 * Replace the marker with a server summary. `null` removes it: the fan has no transaction.
		 *
		 * `verifyUntil` survives a rewrite of the SAME transaction: it is this browser's own note
		 * (a rejoin that superseded a leave re-checks for a late raise), not something the server
		 * sends back.
		 *
		 * @param {string}      sub     The fan.
		 * @param {Object|null} summary The server's `membership` summary.
		 * @return {void}
		 */
		write( sub, summary ) {
			const store = storage();

			// `null` -- "this fan has no transaction" -- removes nothing. It is either a row pruned
			// after retention, where a closed marker is harmless, or a status answer computed
			// before a begin and delivered after it, where removing would lose the live one.
			if ( ! store || ! sub || ! summary || typeof summary.txn !== 'string' ) {
				return;
			}

			try {
				const previous = this.read( sub );

				// Never let an older answer overwrite a newer one. `rev` rises with every new row
				// and every transition on the server, so a stale status read that lands after a
				// begin cannot rewrite the marker the running leg is fenced on.
				if ( previous && Number( previous.rev ) > Number( summary.rev || 0 ) ) {
					return;
				}

				const next = { ...summary };

				// This browser's own note about the SAME transaction survives a rewrite: the
				// rejoin's extra look for a late raise.
				if ( previous && previous.txn === summary.txn && previous.verifyUntil ) {
					next.verifyUntil = previous.verifyUntil;
				}

				store.setItem( keyOf( sub ), JSON.stringify( next ) );
			} catch ( error ) {
				// A marker that cannot be stored costs a resume that happens later. Never a throw on
				// a path a fan is waiting on.
			}
		},

		/**
		 * Merge fields into the current marker, if there is one.
		 *
		 * @param {string} sub    The fan.
		 * @param {Object} fields What to set.
		 * @return {void}
		 */
		patch( sub, fields ) {
			const current = this.read( sub );
			const store = storage();

			if ( ! current || ! store ) {
				return;
			}

			try {
				store.setItem( keyOf( sub ), JSON.stringify( { ...current, ...fields } ) );
			} catch ( error ) {
				// As above.
			}
		},

		/**
		 * An abort the server has not acknowledged: `{txn, code}`, or null.
		 *
		 * **Its own key, not a field on the marker**, because the marker may not know the
		 * transaction at all -- a begin whose answer was lost never wrote it -- and a later status
		 * read would then write the server's `processor_pending` summary for it with nothing to
		 * say the fan was told it failed. Resume checks this first and never finishes a
		 * transaction that is waiting to be undone.
		 *
		 * @param {string} sub The fan.
		 * @return {{txn: string, code: string}|null} The pending abort.
		 */
		pendingAbort( sub ) {
			const store = storage();

			if ( ! store || ! sub ) {
				return null;
			}

			try {
				const value = JSON.parse( store.getItem( `${ ABORT_PREFIX }.${ sub }` ) || 'null' );

				return value && typeof value.txn === 'string' ? value : null;
			} catch ( error ) {
				return null;
			}
		},

		/**
		 * @param {string}      sub   The fan.
		 * @param {Object|null} entry `{txn, code}`, or null to clear.
		 * @return {void}
		 */
		setPendingAbort( sub, entry ) {
			const store = storage();

			if ( ! store || ! sub ) {
				return;
			}

			try {
				if ( entry ) {
					store.setItem( `${ ABORT_PREFIX }.${ sub }`, JSON.stringify( entry ) );
				} else {
					store.removeItem( `${ ABORT_PREFIX }.${ sub }` );
				}
			} catch ( error ) {
				// As above.
			}
		},

		/**
		 * @param {string} sub The fan.
		 * @return {void}
		 */
		clear( sub ) {
			const store = storage();

			if ( ! store || ! sub ) {
				return;
			}

			try {
				store.removeItem( keyOf( sub ) );
			} catch ( error ) {
				// As above.
			}
		},
	};
}

/**
 * Whether a marker has a 3Halves leg due for this browser to run now.
 *
 * @param {Object|null} marker The marker.
 * @param {number}      nowSec Unix seconds.
 * @return {boolean} True when there is a task and its time has come.
 */
export function isDue( marker, nowSec ) {
	if ( ! marker || ! marker.open || ! marker.task ) {
		return false;
	}

	return marker.dueAt === null || marker.dueAt === undefined || Number( marker.dueAt ) <= nowSec;
}

/**
 * Whether a committed rejoin still wants its one extra look for a late withdrawal raise.
 *
 * @param {Object|null} marker The marker.
 * @param {number}      nowSec Unix seconds.
 * @return {boolean} True inside the window.
 */
export function wantsVerify( marker, nowSec ) {
	return Boolean( marker && marker.verifyUntil && Number( marker.verifyUntil ) > nowSec );
}

/**
 * Whether this browser must treat the fan's consent as withdrawn whatever a claim says.
 *
 * The server's `status` already answers `false` for a pending leave (the journal closes the gate
 * there). This is the same rule for the one read path that never asks the server: the ID-token
 * claim (`consent.read_from = 'claim'`), whose value is as old as the token.
 *
 * @param {Object|null} marker The marker.
 * @return {boolean} True for an open leave.
 */
export function forcesDenial( marker ) {
	return Boolean( marker && marker.op === 'leave' && marker.open );
}
